import {GridAutoFlowProps, DisplayProps} from 'styled-system'
import styled from '@emotion/styled'

import {PrimaryLink} from 'src/components/Link'
import {Grid, Flex, Box} from 'src/components/Box'
import {ExtraSmallBody} from 'src/components/text'
import {
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  TikTokIcon,
  YouTubeIcon,
} from 'src/components/icons'
import {localPaths, externalPaths} from 'src/urls'
import useCoreJourneys from 'src/hooks/useCoreJourneys'

interface NavSectionProps {
  title?: string
  links: {name: string; to: string}[]
  gridArea?: string
  flowDirection?: GridAutoFlowProps['gridAutoFlow']
  display?: DisplayProps['display']
}

const FooterLink = ({
  children,
  to,
}: {
  children?: React.ReactNode | React.ReactNode[]
  to: string
}) => (
  <PrimaryLink typography="extraSmallBody" to={to}>
    {children}
  </PrimaryLink>
)

const SocialLink = styled.a(({theme}) => ({
  '& + &': {
    marginLeft: theme.space[3],
  },
}))

const NavSection = ({
  title,
  links,
  gridArea,
  flowDirection,
  display,
}: NavSectionProps) => (
  <Grid
    display={display}
    gridAutoRows="min-content"
    gridArea={gridArea}
    gridGap="1"
    justifyContent="flex-start"
  >
    {title && <ExtraSmallBody color="grey5">{title}</ExtraSmallBody>}
    <Grid
      gridAutoFlow={flowDirection ?? 'row'}
      gridRowGap="1"
      gridColumnGap="3"
    >
      {links.map(({name, to}) => (
        <FooterLink key={name} to={to}>
          {name}
        </FooterLink>
      ))}
    </Grid>
  </Grid>
)

const AboutUs = ({gridArea}: {gridArea: string}) => (
  <Flex gridArea={gridArea} flexDirection={{_: 'column', lg: 'row'}}>
    <Box mb={{_: '3', lg: 0}}>
      <Box as="p" gridArea="description" mr={{lg: 2}}>
        Get Schooled helps young people get to college, find first jobs, and
        succeed in both.
      </Box>
      <Flex mt="3">
        <SocialLink
          href={externalPaths.facebook}
          rel="noopener noreferrer"
          target="_blank"
          aria-label="GetSchooled Facebook"
        >
          <FacebookIcon />
        </SocialLink>

        <SocialLink
          href={externalPaths.twitter}
          rel="noopener noreferrer"
          target="_blank"
          aria-label="GetSchooled Twitter"
        >
          <TwitterIcon />
        </SocialLink>
        <SocialLink
          href={externalPaths.instagram}
          rel="noopener noreferrer"
          target="_blank"
          aria-label="GetSchooled Instagram"
        >
          <InstagramIcon />
        </SocialLink>
        <SocialLink
          href={externalPaths.tiktok}
          rel="noopener noreferrer"
          target="_blank"
          aria-label="GetSchooled TikTok"
        >
          <TikTokIcon />
        </SocialLink>
        <SocialLink
          href={externalPaths.youtube}
          rel="noopener noreferrer"
          target="_blank"
          aria-label="GetSchooled YouTube"
        >
          <YouTubeIcon />
        </SocialLink>
      </Flex>
    </Box>

    <Box minWidth={{lg: 120}}>
      <NavSection
        flowDirection={{_: 'column', lg: 'row'}}
        gridArea="nav"
        links={[
          {name: 'About Us', to: localPaths.aboutUs},
          {name: 'Our Team', to: localPaths.ourTeam},
          {name: 'Board of Directors', to: localPaths.boardOfDirectors},
          {name: 'FAQ', to: localPaths.faq},
        ]}
      />
    </Box>
  </Flex>
)

const Footer = () => {
  const coreJourneys = useCoreJourneys()
  const currentYear = new Date().getUTCFullYear()

  return (
    <Grid
      gridTemplateColumns={{
        _: '1fr 1fr',
        md: '1fr 1fr 1fr 1fr',
        lg: '3fr 1fr 1fr 1fr',
      }}
      gridTemplateAreas={{
        _: "'about about' 'goal resources' 'educators terms' 'copyright copyright'",
        md: "'about about about about' 'goal resources educators terms' 'copyright copyright copyright copyright'",
        lg: "'about goal resources educators' 'copyright copyright copyright copyright'",
      }}
      gridGap="3"
      bg="grey1"
      width="100%"
    >
      <AboutUs gridArea="about" />
      <NavSection
        gridArea="goal"
        title="Pursue Your Goal"
        links={coreJourneys.map((j) => ({
          name: j.name,
          to: j.permalink,
        }))}
      />
      <NavSection
        gridArea="resources"
        title="Resources"
        links={[
          {name: 'Text Us', to: localPaths.sendText},
          {
            name: 'College Application Help',
            to: localPaths.collegeAppHelp,
          },
          {name: 'Resume Help', to: localPaths.resumeHelp},
          {name: 'Job Search', to: localPaths.jobSearch},
        ]}
      />
      <NavSection
        gridArea="educators"
        title="Educators"
        links={[
          {name: 'Educator Resources', to: localPaths.educatorResources},
          {name: 'Join Our Community', to: localPaths.educatorJoinCommunity},
        ]}
      />

      <NavSection
        gridArea="terms"
        display={{_: 'grid', lg: 'none'}}
        links={[
          {name: 'Privacy Policy', to: localPaths.privacyPolicy},
          {name: 'Terms & Conditions', to: localPaths.termsAndConditions},
          {name: 'Contact Us', to: localPaths.contactUs},
        ]}
      />

      <Grid
        gridAutoFlow="column"
        gridGap="2"
        justifyContent={{_: 'center', lg: 'flex-start'}}
        gridArea="copyright"
      >
        <ExtraSmallBody color="grey5">
          ©{currentYear} Get Schooled
        </ExtraSmallBody>
        <NavSection
          flowDirection="column"
          display={{_: 'none', lg: 'grid'}}
          links={[
            {name: 'Privacy Policy', to: localPaths.privacyPolicy},
            {name: 'Terms & Conditions', to: localPaths.termsAndConditions},
            {name: 'Contact Us', to: localPaths.contactUs},
          ]}
        />
      </Grid>
    </Grid>
  )
}

export default Footer
